import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Icon,
} from "@elit-hair/elit-ui";
import { useTranslation } from "react-i18next";
import { TbCheck, TbMicrophone, TbSpeakerphone } from "react-icons/tb";

import type { AudioDeviceAutoSwitch } from "@/utils/audio-device-storage.utils";

type AudioDeviceAutoSwitchDialogProps = {
    open: boolean;
    autoSwitches: AudioDeviceAutoSwitch[];
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
    onCancel: () => void;
};

export function AudioDeviceAutoSwitchDialog({
    open,
    autoSwitches,
    onOpenChange,
    onConfirm,
    onCancel,
}: AudioDeviceAutoSwitchDialogProps) {
    const { t } = useTranslation();

    return (
        <Dialog open={open} onOpenChange={(next) => (!next ? onCancel() : onOpenChange(next))}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("audio_device_auto_switch_title")}</DialogTitle>
                    <DialogDescription>{t("audio_device_auto_switch_description")}</DialogDescription>
                </DialogHeader>

                <ul className="space-y-3 py-2">
                    {autoSwitches.map((item) => {
                        const isInput = item.kind === "audioinput";
                        const lineKey = isInput
                            ? "audio_device_auto_switch_input_line"
                            : "audio_device_auto_switch_output_line";
                        const previousName = item.previousLabel ?? t("audio_device_auto_switch_none");

                        return (
                            <li
                                key={`${item.kind}-${item.newDeviceId}`}
                                className="flex gap-2 rounded-lg border border-border/50 p-3 text-sm"
                            >
                                <Icon
                                    icon={isInput ? TbMicrophone : TbSpeakerphone}
                                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                                />
                                <span>
                                    {t(lineKey, {
                                        previous: previousName,
                                        next: item.newLabel,
                                    })}
                                </span>
                            </li>
                        );
                    })}
                </ul>

                <DialogFooter>
                    <Button variant="outline" onClick={onCancel}>
                        {t("cancel")}
                    </Button>
                    <Button onClick={onConfirm}>
                        <Icon icon={TbCheck} className="mr-2 h-4 w-4" />
                        {t("audio_device_auto_switch_confirm")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
